import React, { useState } from 'react';
import { 
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Container,
  IconButton,
  useTheme,
  TextField,
  ButtonGroup,
  Tooltip,
  Chip,
  Snackbar,
  Alert,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { motion } from 'framer-motion';
import { AnimatePresence } from 'framer-motion';
import NewsEvents from './NewsEvents';
import ShareIcon from '@mui/icons-material/Share';
import html2canvas from 'html2canvas';
import DownloadIcon from '@mui/icons-material/Download';

const MotionCard = motion(Card);

function Dashboard({ coins, wallet, onBuy, onSell, portfolio, darkMode, setDarkMode }) {
  const theme = useTheme();
  const [amounts, setAmounts] = useState({});
  const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });
  const [shareableCard, setShareableCard] = useState(null);
  const [previewModal, setPreviewModal] = useState({
    open: false,
    imageData: null,
    tradeDetails: null
  });

  const handleAmountChange = (coinId, value) => {
    setAmounts(prev => ({
      ...prev,
      [coinId]: Math.max(0, parseInt(value) || 0)
    }));
  };

  const getOwnedAmount = (coinId) => {
    const position = portfolio.find(p => p.coinId === coinId);
    return position ? position.amount : 0;
  };

  const getProfitLoss = (coin) => {
    const position = portfolio.find(p => p.coinId === coin.id);
    if (!position) return 0;
    return ((coin.price - position.avgPrice) / position.avgPrice) * 100;
  };

  // Add price change animation
  const priceVariants = {
    up: { color: '#00ff00', transition: { duration: 0.3 } },
    down: { color: '#ff0000', transition: { duration: 0.3 } }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({
      open: true,
      message,
      type,
    });
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setNotification({ ...notification, open: false });
  };

  const handlePercentageSell = (coin, percentage) => {
    const position = portfolio.find(p => p.coinId === coin.id);
    if (!position) {
      showNotification('No coins to sell!', 'error');
      return;
    }
    
    const sellAmount = Math.floor(position.amount * (percentage / 100));
    if (sellAmount > 0) {
      const profit = getProfitLoss(coin);
      onSell(coin, sellAmount);
      
      // Use the same share notification as handleCustomSell
      setNotification({
        open: true,
        message: (
          <Box display="flex" alignItems="center" gap={2}>
            <Typography>{`Sold ${sellAmount} ${coin.name} (${percentage}%) with ${profit.toFixed(2)}% profit!`}</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<ShareIcon />}
              onClick={() => handleShareClick(coin, sellAmount, profit)}
              sx={{ 
                background: 'linear-gradient(45deg, #1DA1F2 30%, #1a91da 90%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1a91da 30%, #1778b5 90%)',
                }
              }}
            >
              Share
            </Button>
          </Box>
        ),
        type: 'success'
      });
    }
  };

  // Modify createTradeCard to return both the element and canvas
  const createTradeCard = async (coin, amount, profit) => {
    const tradeCard = document.createElement('div');
    tradeCard.style.padding = '20px';
    tradeCard.style.background = darkMode ? '#1a1a1a' : '#ffffff';
    tradeCard.style.borderRadius = '12px';
    tradeCard.style.width = '300px';
    
    tradeCard.innerHTML = `
      <div style="text-align: center; font-family: Arial;">
        <h2 style="color: ${darkMode ? '#FFD700' : '#FF6B6B'}">🦍 MoonJungle Battle Report 🚀</h2>
        <p style="font-size: 24px; margin: 10px 0;">Just ${profit >= 0 ? 'conquered' : 'lost'} ${Math.abs(profit).toFixed(2)}%</p>
        <p style="font-size: 18px;">Trading ${amount} $${coin.name}</p>
        <p style="font-size: 14px; color: #666;">Join the Memecoin Wars at MoonJungle.com</p>
      </div>
    `;

    document.body.appendChild(tradeCard);
    const canvas = await html2canvas(tradeCard);
    document.body.removeChild(tradeCard);
    return canvas;
  };

  const handleShareClick = async (coin, amount, profit) => {
    const canvas = await createTradeCard(coin, amount, profit);
    setPreviewModal({
      open: true,
      imageData: canvas.toDataURL('image/png'),
      tradeDetails: { coin, amount, profit }
    });
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.download = 'trade-card.png';
    link.href = previewModal.imageData;
    link.click();
  };

  const handleShare = () => {
    const { coin, amount, profit } = previewModal.tradeDetails;
    const text = `🦍 Battle Report: Just ${profit >= 0 ? 'conquered' : 'lost'} ${Math.abs(profit).toFixed(2)}% trading ${amount} $${coin.name} in the Memecoin Wars!\n\n🚀 Join the battle: moonjungle.lol`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(twitterUrl, '_blank');
    setPreviewModal({ open: false, imageData: null, tradeDetails: null });
  };

  // Update notification message to use handleShareClick
  const createShareNotification = (coin, amount, profit) => ({
    open: true,
    message: (
      <Box display="flex" alignItems="center" gap={2}>
        <Typography>{`Sold ${amount} ${coin.name} with ${profit.toFixed(2)}% profit!`}</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<ShareIcon />}
          onClick={() => handleShareClick(coin, amount, profit)}
          sx={{ 
            background: 'linear-gradient(45deg, #1DA1F2 30%, #1a91da 90%)',
            color: 'white',
            '&:hover': {
              background: 'linear-gradient(45deg, #1a91da 30%, #1778b5 90%)',
            }
          }}
        >
          Share
        </Button>
      </Box>
    ),
    type: 'success'
  });

  // Update handleCustomSell and handlePercentageSell to use createShareNotification
  const handleCustomSell = (coin) => {
    const amount = amounts[coin.id];
    if (!amount || amount <= 0) {
      showNotification('Please enter a valid amount', 'error');
      return;
    }
    
    const position = portfolio.find(p => p.coinId === coin.id);
    if (!position || position.amount < amount) {
      showNotification('Not enough coins to sell!', 'error');
      return;
    }

    const profit = getProfitLoss(coin);
    onSell(coin, amount);
    setNotification(createShareNotification(coin, amount, profit));
    setAmounts(prev => ({ ...prev, [coin.id]: '' }));
  };

  const handleBuyAction = (coin) => {
    const amount = amounts[coin.id];
    if (!amount || amount <= 0) {
      showNotification('Please enter a valid amount', 'error');
      return;
    }

    const cost = coin.price * amount;
    if (cost > wallet) {
      showNotification('Not enough funds!', 'error');
      return;
    }

    onBuy(coin, amount);
    showNotification(`Bought ${amount} ${coin.name}`);
    setAmounts(prev => ({ ...prev, [coin.id]: '' }));
  };

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" component="h2" sx={{ 
            fontWeight: 700,
            background: darkMode ? 
              'linear-gradient(45deg, #FFD700 30%, #FFA500 90%)' : 
              'linear-gradient(45deg, #FFD700 30%, #FF6B6B 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 20px rgba(255, 215, 0, 0.3)'
          }}>
            Memecoin Wars 🦍🚀
          </Typography>
          <Typography 
            variant="subtitle2" 
            component="a" 
            href="https://apeout.fun" 
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              textDecoration: 'none',
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                textDecoration: 'underline'
              }
            }}
          >
            By Apeout
          </Typography>
        </Box>
        
        {/* Updated wallet display */}
        <Box display="flex" gap={2} alignItems="center">
          <IconButton onClick={() => setDarkMode(!darkMode)} color="primary">
            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
          <motion.div whileHover={{ scale: 1.05 }}>
            <Chip 
              label={`💰 $${wallet.toFixed(2)}`}
              color="primary"
              sx={{ 
                fontSize: '1.1rem', 
                padding: '20px 10px',
                background: 'linear-gradient(45deg, #2196F3 30%, #00ff00 90%)',
                fontWeight: 'bold'
              }}
            />
          </motion.div>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {coins.map((coin) => {
          const profitLoss = getProfitLoss(coin);
          const ownedAmount = getOwnedAmount(coin.id);
          
          return (
            <Grid item xs={12} sm={6} md={4} key={coin.id}>
              <MotionCard
                whileHover={{ scale: 1.03, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}
                whileTap={{ scale: 0.98 }}
                sx={{
                  background: theme.palette.mode === 'dark' 
                    ? 'linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.1))'
                    : 'linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.95))',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.18)',
                }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                      {coin.name} {coin.trend}
                    </Typography>
                    <motion.div
                      animate={coin.trend === '📈' ? 'up' : 'down'}
                      variants={priceVariants}
                    >
                      <IconButton 
                        size="small" 
                        color={coin.trend === '📈' ? 'success' : 'error'}
                        sx={{ background: coin.trend === '📈' ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)' }}
                      >
                        {coin.trend === '📈' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                      </IconButton>
                    </motion.div>
                  </Box>
                  
                  <motion.div
                    animate={coin.trend === '📈' ? 'up' : 'down'}
                    variants={priceVariants}
                  >
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                      ${coin.price.toFixed(4)}
                    </Typography>
                  </motion.div>
                  
                  {ownedAmount > 0 && (
                    <Box mb={2}>
                      <Chip
                        label={`Owned: ${ownedAmount}`}
                        color={profitLoss >= 0 ? 'success' : 'error'}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Chip
                        label={`${profitLoss.toFixed(2)}%`}
                        color={profitLoss >= 0 ? 'success' : 'error'}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  )}
                  
                  <Stack spacing={1}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Amount"
                      variant="outlined"
                      size="small"
                      value={amounts[coin.id] || ''}
                      onChange={(e) => handleAmountChange(coin.id, e.target.value)}
                    />
                    
                    <ButtonGroup fullWidth variant="contained">
                      <Button 
                        onClick={() => handleBuyAction(coin)}
                        sx={{
                          background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                        }}
                      >
                        Buy
                      </Button>
                      <Button 
                        onClick={() => handleCustomSell(coin)}
                        sx={{
                          background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)',
                        }}
                      >
                        Sell
                      </Button>
                    </ButtonGroup>

                    {ownedAmount > 0 && (
                      <ButtonGroup fullWidth size="small" variant="outlined">
                        <Button onClick={() => handlePercentageSell(coin, 25)}>25%</Button>
                        <Button onClick={() => handlePercentageSell(coin, 50)}>50%</Button>
                        <Button onClick={() => handlePercentageSell(coin, 100)}>100%</Button>
                      </ButtonGroup>
                    )}
                  </Stack>
                </CardContent>
              </MotionCard>
            </Grid>
          );
        })}
      </Grid>

      {/* Add NewsEvents component */}
      <NewsEvents darkMode={darkMode} />

      {/* Add Preview Modal */}
      <Dialog 
        open={previewModal.open} 
        onClose={() => setPreviewModal({ open: false, imageData: null, tradeDetails: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center' }}>Share Your Battle Report 🦍</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            {previewModal.imageData && (
              <img 
                src={previewModal.imageData} 
                alt="Trade Card" 
                style={{ 
                  maxWidth: '100%',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }} 
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
          >
            Download
          </Button>
          <Button
            variant="contained"
            startIcon={<ShareIcon />}
            onClick={handleShare}
            sx={{ 
              background: 'linear-gradient(45deg, #1DA1F2 30%, #1a91da 90%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(45deg, #1a91da 30%, #1778b5 90%)',
              }
            }}
          >
            Share on Twitter
          </Button>
        </DialogActions>
      </Dialog>

      <AnimatePresence>
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <Alert 
              onClose={handleClose} 
              severity={notification.type}
              variant="filled"
              sx={{ 
                minWidth: '400px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                borderRadius: '12px',
                '& .MuiAlert-message': {
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }
              }}
            >
              {notification.message}
            </Alert>
          </motion.div>
        </Snackbar>
      </AnimatePresence>
    </Container>
  );
}

export default Dashboard;